import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
} from '@ads/components-react';
import { ArrowPathIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { EnrollmentStats } from '@/features/student-management/hooks/useEnrollmentList';

export interface EnrollmentListTemplateProps {
  readonly filterPanel?: ReactNode;
  readonly children: ReactNode;
  readonly stats?: EnrollmentStats;
  readonly onNewEnrollment?: () => void;
  readonly onBatchStatusChange?: () => void;
}

function StatCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="flex flex-col gap-quark rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini shadow-level1">
      <span className="ts-caption-xxs text-ctx-content-placeholder">{label}</span>
      <span className="ts-heading-md text-ctx-content-title">{value}</span>
      <span className="ts-caption-xxs text-ctx-content-placeholder">{sub}</span>
    </div>
  );
}

export function EnrollmentListTemplate({
  filterPanel,
  children,
  stats,
  onNewEnrollment,
  onBatchStatusChange,
}: EnrollmentListTemplateProps) {
  const { t } = useTranslation('personManagement');

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-nano">
        <Breadcrumb>
          <BreadcrumbLink href="/">
            <Link to="/">
              <BreadcrumbIcon />
            </Link>
          </BreadcrumbLink>
          <BreadcrumbPage>{t('breadcrumb.enrollments')}</BreadcrumbPage>
        </Breadcrumb>

        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-xs">
          <div className="flex flex-col gap-quark">
            <h1 className="ts-heading-lg text-ctx-content-title">{t('enrollmentList.title')}</h1>
            <p className="ts-paragraph-xs text-ctx-content-base">{t('enrollmentList.subtitle')}</p>
          </div>
          <div className="flex shrink-0 gap-xs">
            <Button
              hierarchy="secondary"
              size="md"
              leadingIcon={ArrowPathIcon}
              onClick={onBatchStatusChange}
            >
              {t('enrollmentActions.batchStatusChange')}
            </Button>
            <Button hierarchy="primary" size="md" leadingIcon={PlusIcon} onClick={onNewEnrollment}>
              {t('enrollmentList.newEnrollment')}
            </Button>
          </div>
        </div>
      </div>

      {/* Filter toolbar */}
      {filterPanel && (
        <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
          {filterPanel}
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-xs lg:grid-cols-4">
          <StatCard
            label={t('enrollmentList.stats.totalFiltered')}
            value={String(stats.totalCount)}
            sub={t('enrollmentList.stats.totalFilteredSub')}
          />
          <StatCard
            label={t('enrollmentList.stats.classes')}
            value={String(stats.classesCount)}
            sub={t('enrollmentList.stats.classesSub', { count: stats.classesCount })}
          />
          <StatCard
            label={t('enrollmentList.stats.courses')}
            value={String(stats.coursesCount)}
            sub={t('enrollmentList.stats.coursesSub', { count: stats.coursesCount })}
          />
          <StatCard
            label={t('enrollmentList.stats.polos')}
            value={String(stats.polosCount)}
            sub={t('enrollmentList.stats.polosSub', { count: stats.polosCount })}
          />
        </div>
      )}

      {children}
    </div>
  );
}
